/**
 * Astra Auto storefront enhancements.
 * Every feature is optional; the storefront remains usable without JavaScript.
 */
(function () {
  'use strict';

  if (window.__astraAutoThemeLoaded) return;
  window.__astraAutoThemeLoaded = true;

  var doc = document;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var revealObserver;

  doc.documentElement.classList.add('aa-js');

  function all(selector, scope) {
    return Array.prototype.slice.call((scope || doc).querySelectorAll(selector));
  }

  function once(element, feature) {
    var key = 'aa' + feature;
    if (element.dataset[key] === 'ready') return false;
    element.dataset[key] = 'ready';
    return true;
  }

  function reveal(element) {
    element.classList.add('aa-is-visible');
    revealObserver && revealObserver.unobserve(element);
  }

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;

    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    return revealObserver;
  }

  function initReveals(scope) {
    var elements = all('[data-aa-reveal], [data-reveal]', scope);
    var observer = getRevealObserver();

    elements.forEach(function (element) {
      if (!once(element, 'Reveal')) return;

      var stagger = element.closest('[data-aa-stagger], [data-stagger]');
      var siblings = stagger ? all('[data-aa-reveal], [data-reveal]', stagger) : [];
      var index = siblings.indexOf(element);
      var explicitDelay = parseInt(element.getAttribute('data-aa-reveal-delay'), 10);
      var delay = Number.isFinite(explicitDelay) ? explicitDelay : Math.max(index, 0) * 70;

      element.style.setProperty('--aa-reveal-delay', Math.min(delay, 420) + 'ms');

      if (reducedMotion.matches || !observer) {
        element.classList.add('aa-is-visible');
        return;
      }

      element.classList.add('aa-reveal-pending');
      observer.observe(element);
    });
  }

  function initHeaders(scope) {
    var headers = all('[data-aa-header], .aa-site-header, #header-group', scope);
    if (!headers.length) return;

    var update = function () {
      var scrolled = window.scrollY > 12;
      headers.forEach(function (header) {
        header.classList.toggle('aa-is-scrolled', scrolled);
      });
    };

    update();
    if (!window.__astraAutoHeaderListener) {
      window.__astraAutoHeaderListener = true;
      window.addEventListener('scroll', update, { passive: true });
    }
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function parseCompatibility(container) {
    var raw = container.getAttribute('data-aa-compatible') || container.getAttribute('data-aa-fitment-compatible');
    if (!raw) return null;

    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      return raw.split(',').map(function (item) { return item.trim(); }).filter(Boolean);
    }
  }

  function isCompatible(config, values) {
    if (!config) return null;
    var selected = Object.keys(values).map(function (key) { return normalize(values[key]); }).join('|');

    return config.some(function (entry) {
      if (typeof entry === 'string') {
        var candidate = normalize(entry).replace(/\s*\|\s*/g, '|');
        return selected === candidate || selected.indexOf(candidate) !== -1 || candidate.indexOf(selected) !== -1;
      }

      if (!entry || typeof entry !== 'object') return false;
      return Object.keys(entry).every(function (key) {
        return !values[key] || normalize(entry[key]) === normalize(values[key]);
      });
    });
  }

  function initFitment(scope) {
    all('[data-aa-fitment]', scope).forEach(function (container) {
      if (!once(container, 'Fitment')) return;

      var fields = all('[data-aa-fitment-field], select[name], input[name]', container).filter(function (field) {
        return field.type !== 'hidden' && field.type !== 'submit' && field.type !== 'button';
      });
      var result = container.querySelector('[data-aa-fitment-result], [data-aa-fitment-status]');
      var submit = container.querySelector('[data-aa-fitment-submit]');
      var compatibility = parseCompatibility(container);

      if (!fields.length) return;

      function update() {
        var values = {};
        var complete = true;

        fields.forEach(function (field, index) {
          var key = field.getAttribute('data-aa-fitment-field') || field.name || ('field-' + index);
          values[key] = field.value;
          if (field.required && !field.value) complete = false;
        });

        var match = complete ? isCompatible(compatibility, values) : null;
        var state = !complete ? 'incomplete' : match === false ? 'no-match' : match === true ? 'match' : 'selected';
        var message;

        if (state === 'incomplete') {
          message = container.getAttribute('data-aa-incomplete-message') || 'Choose your vehicle details to check fitment.';
        } else if (state === 'match') {
          message = container.getAttribute('data-aa-match-message') || 'Yes — this product matches your selected vehicle.';
        } else if (state === 'no-match') {
          message = container.getAttribute('data-aa-no-match-message') || 'This product does not match that selection. Please contact us and we will help.';
        } else {
          message = container.getAttribute('data-aa-selected-message') || 'Vehicle details selected. Check the product fitment note before ordering.';
        }

        container.setAttribute('data-aa-fitment-state', state);
        if (result) {
          result.setAttribute('data-aa-fitment-status', state);
          result.textContent = message;
          result.setAttribute('role', state === 'no-match' ? 'alert' : 'status');
          result.setAttribute('aria-live', 'polite');
        }

        if (submit) {
          var disabled = state === 'incomplete' || state === 'no-match';
          submit.toggleAttribute('disabled', disabled);
          submit.setAttribute('aria-disabled', String(disabled));
        }

        container.dispatchEvent(new CustomEvent('astra:fitmentchange', {
          bubbles: true,
          detail: { state: state, values: values }
        }));
      }

      fields.forEach(function (field) {
        field.addEventListener('change', update);
        field.addEventListener('input', update);
      });
      update();
    });
  }

  function initFaqs(scope) {
    all('[data-aa-faq][data-aa-accordion="single"], [data-aa-faq-single], .aa-product-faq[data-aa-accordion="single"]', scope).forEach(function (group) {
      if (!once(group, 'Faq')) return;

      all('details', group).forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          all('details[open]', group).forEach(function (other) {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  function initFaqPage() {
    if (!doc.body.classList.contains('aa-page-handle--faqs')) return;

    all('#MainContent rte-formatter').forEach(function (formatter) {
      if (!once(formatter, 'PageFaq')) return;

      var children = Array.prototype.slice.call(formatter.children);
      var firstQuestion = children.find(function (child) { return child.tagName === 'H2'; });
      if (!firstQuestion) return;

      var accordion = doc.createElement('div');
      accordion.className = 'aa-page-faq__accordion';
      formatter.insertBefore(accordion, firstQuestion);

      var item;
      var answer;

      children.forEach(function (child) {
        if (child.tagName === 'H2') {
          item = doc.createElement('details');
          item.className = 'aa-page-faq__item';

          var summary = doc.createElement('summary');
          summary.className = 'aa-page-faq__summary';

          var question = doc.createElement('span');
          question.className = 'aa-page-faq__question';
          question.textContent = child.textContent.trim();

          var icon = doc.createElement('span');
          icon.className = 'aa-page-faq__icon';
          icon.setAttribute('aria-hidden', 'true');

          answer = doc.createElement('div');
          answer.className = 'aa-page-faq__answer';

          summary.appendChild(question);
          summary.appendChild(icon);
          item.appendChild(summary);
          item.appendChild(answer);
          accordion.appendChild(item);
          child.remove();
          return;
        }

        if (answer) answer.appendChild(child);
      });
    });
  }

  function initEditorialGlassPages() {
    var body = doc.body;
    var isEditorialPage = [
      'aa-page-handle--contact',
      'aa-page-handle--fitment-guide',
      'aa-page-handle--about-us',
      'aa-page-handle--about-astra-auto'
    ].some(function (className) { return body.classList.contains(className); });
    var isPolicyPage = body.classList.contains('aa-page--policy') || !!doc.querySelector('.shopify-policy__container');

    if (!isEditorialPage && !isPolicyPage) return;

    body.classList.add('aa-editorial-glass');

    var hero = doc.querySelector('#MainContent .section.page-width-content, #MainContent .shopify-policy__container');
    if (hero) hero.classList.add('aa-editorial-glass__hero');

    var selectors = isPolicyPage
      ? ['#MainContent .shopify-policy__body > .rte']
      : [
          '#MainContent rte-formatter.rte',
          '#MainContent .shopify-block.rte rte-formatter',
          '#MainContent .page-content > rte-formatter',
          '#MainContent .page-content.rte'
        ];

    selectors.forEach(function (selector) {
      all(selector).forEach(function (formatter) {
        if (!once(formatter, 'EditorialGlass')) return;
        if (formatter.closest('.aa-page-faq__answer')) return;

        formatter.classList.add('aa-editorial-glass__copy');
        var children = Array.prototype.slice.call(formatter.children);
        var firstSectionHeading = children.find(function (child) { return child.tagName === 'H2'; });
        if (!firstSectionHeading) return;

        var deck = doc.createElement('div');
        deck.className = 'aa-editorial-glass__deck';
        formatter.insertBefore(deck, firstSectionHeading);

        var card;
        children.forEach(function (child) {
          if (child.tagName === 'H2') {
            card = doc.createElement('section');
            card.className = 'aa-editorial-glass__card';
            child.classList.add('aa-editorial-glass__card-title');
            deck.appendChild(card);
          }
          if (card) card.appendChild(child);
        });
      });
    });

    if (body.classList.contains('aa-page-handle--contact')) {
      all('#MainContent .contact-form').forEach(function (form) {
        form.classList.add('aa-editorial-glass__form');
      });
    }
  }

  function initGalleries(scope) {
    all('[data-aa-gallery]', scope).forEach(function (gallery) {
      if (!once(gallery, 'Gallery')) return;

      var items = all('[data-aa-gallery-item]', gallery);
      var controls = gallery.parentElement ? all('[data-aa-gallery-dot], .aa-gallery-dot', gallery.parentElement) : [];
      if (items.length < 2 || !controls.length) return;

      function activate(index) {
        controls.forEach(function (control, controlIndex) {
          var active = controlIndex === index;
          control.classList.toggle('aa-is-active', active);
          control.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }

      controls.forEach(function (control, index) {
        control.addEventListener('click', function () {
          items[index] && items[index].scrollIntoView({
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        });
      });

      if ('IntersectionObserver' in window) {
        var galleryObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) activate(items.indexOf(entry.target));
          });
        }, { root: gallery, threshold: 0.65 });
        items.forEach(function (item) { galleryObserver.observe(item); });
      }

      activate(0);
    });
  }

  function initStickyCarts(scope) {
    all('[data-aa-sticky-cart]', scope).forEach(function (sticky) {
      if (!once(sticky, 'StickyCart')) return;

      var selector = sticky.getAttribute('data-aa-sticky-target');
      var source = selector ? doc.querySelector(selector) : doc.querySelector('form[action*="/cart/add"] [name="add"], product-form [type="submit"]');
      var stickyButton = sticky.querySelector('[data-aa-sticky-submit], button');
      if (!source || !stickyButton) return;

      var form = source.closest('form');

      function sync() {
        var disabled = source.disabled || source.getAttribute('aria-disabled') === 'true';
        stickyButton.disabled = disabled;
        stickyButton.setAttribute('aria-disabled', String(disabled));
        if (source.textContent.trim()) stickyButton.textContent = source.textContent.trim();
      }

      function setVisible(visible) {
        sticky.classList.toggle('aa-is-visible', visible);
        sticky.setAttribute('data-aa-visible', String(visible));
      }

      stickyButton.addEventListener('click', function () {
        if (stickyButton.disabled) return;
        if (form && typeof form.requestSubmit === 'function') form.requestSubmit(source);
        else source.click();
      });

      new MutationObserver(sync).observe(source, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true
      });
      sync();

      if ('IntersectionObserver' in window) {
        var cartObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) { setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0); });
        }, { threshold: 0.01 });
        cartObserver.observe(source);
      }
    });
  }

  function initSmoothLinks(scope) {
    all('[data-aa-scroll][href^="#"]', scope).forEach(function (link) {
      if (!once(link, 'Scroll')) return;

      link.addEventListener('click', function (event) {
        var id = link.getAttribute('href');
        var target = id && id.length > 1 ? doc.querySelector(id) : null;
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
        if (target.tabIndex < 0) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        window.history.replaceState(null, '', id);
      });
    });
  }

  function hideInternalFitmentOptions(scope) {
    all('dt', scope).forEach(function (term) {
      if (normalize(term.textContent).replace(':', '') !== 'screen size') return;
      term.hidden = true;
      if (term.nextElementSibling && term.nextElementSibling.tagName === 'DD') {
        term.nextElementSibling.hidden = true;
      }
    });
  }

  function init(scope) {
    var root = scope || doc;
    initReveals(root);
    initHeaders(root);
    initFitment(root);
    initFaqs(root);
    initFaqPage();
    initEditorialGlassPages();
    initGalleries(root);
    initStickyCarts(root);
    initSmoothLinks(root);
    hideInternalFitmentOptions(root);
    doc.documentElement.classList.add('aa-is-ready');
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { init(doc); }, { once: true });
  else init(doc);

  doc.addEventListener('shopify:section:load', function (event) { init(event.target); });

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) hideInternalFitmentOptions(node);
      });
    });
  }).observe(doc.documentElement, { childList: true, subtree: true });
})();
